import { Link, Outlet, useRouterState } from '@tanstack/react-router'
import { PAGES, pagePath } from '../lib/pages'
import { LogoIcon } from './Icons'
import { ThemeToggle } from './ThemeToggle'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '../lib/api'
import { useAuth } from '../lib/auth'
import { CustomerSwitcher } from './CustomerSwitcher'
import { GlobalSearch } from './GlobalSearch'
import { HealthIndicator } from './HealthIndicator'
import { UserMenu } from './UserMenu'

interface AppShellProps {
  /** Kunden-Slug aus der URL, null bedeutet "Alle Kunden". */
  customerSlug: string | null
}

/**
 * Gerüst aus linker Navigation (232 px) und Kopfzeile (60 px) laut
 * docs/design.md. Der Kundenkontext steckt in der URL und bestimmt, wohin die
 * Navigationslinks zeigen.
 */
export function AppShell({ customerSlug }: AppShellProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const { can } = useAuth()

  // Die Zahl offener Alarme für das Abzeichen in der Navigation.
  const { data: openAlerts } = useQuery({
    queryKey: ['alerts', 'badge'],
    queryFn: ({ signal }) => apiGet<{ id: string }[]>('/alerts', signal),
    refetchInterval: 30_000,
    enabled: can('view'),
  })

  const visible = PAGES.filter((page) => !page.permission || can(page.permission))
  // Aktiv ist der Eintrag, dessen Adresse am genauesten passt.
  const activePath = activeNavPath(
    pathname,
    visible.map((page) => pagePath(page.segment, customerSlug)),
  )

  return (
    <div className="app">
      <a className="skip-link" href="#inhalt">
        Zum Inhalt springen
      </a>

      <nav className="sidebar" aria-label="Hauptnavigation">
        <div className="brand">
          <LogoIcon />
          <span>Netzwarte</span>
        </div>
        {visible.map((page) => {
          const to = pagePath(page.segment, customerSlug)
          const current = to === activePath
          const badge = page.badge === 'alerts' ? (openAlerts?.length ?? 0) : 0
          return (
            <Link
              key={page.segment || 'uebersicht'}
              to={to}
              className="nav-link"
              aria-current={current ? 'page' : undefined}
            >
              <span>{page.label}</span>
              {badge > 0 ? (
                <span className="nav-badge" aria-label={`${badge} offene Alarme`}>
                  {badge}
                </span>
              ) : null}
            </Link>
          )
        })}
        <div className="sidebar-foot">
          <HealthIndicator variant="sidebar" />
        </div>
      </nav>

      <div className="main-col">
        <header className="topbar">
          <CustomerSwitcher customerSlug={customerSlug} />
          <GlobalSearch />
          <div className="grow" />
          <HealthIndicator variant="topbar" />
          <ThemeToggle />
          <UserMenu />
        </header>

        <main className="content" id="inhalt">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

function normalise(path: string): string {
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path
}

/**
 * Der Eintrag mit der längsten passenden Adresse ist der aktive. Ein
 * Vergleich auf Gleichheit ließe Unterseiten (ein einzelnes Gerät) ohne
 * Markierung, ein Vergleich mit startsWith allein würde "Übersicht" auf
 * jeder Seite mitmarkieren – auf genau das ist es im Betrieb hinausgelaufen.
 */
export function activeNavPath(pathname: string, candidates: string[]): string | null {
  const path = normalise(pathname)
  let best: string | null = null
  for (const candidate of candidates) {
    const to = normalise(candidate)
    const matches = path === to || path.startsWith(to === '/' ? '/' : to + '/')
    if (!matches) continue
    if (best === null || to.length > normalise(best).length) best = candidate
  }
  return best
}
